/**
 * Resolve a document's display name (filename) from its UUID. Used by
 * evidence/citation surfaces (Research evidence list, Case Summary
 * per-investigation evidence) so the user sees ``invoice_2024.pdf`` instead
 * of an opaque hash like ``8db8c06``.
 *
 * Implementation notes:
 *   - Keyed by ``docId`` so React Query naturally dedupes across rows that
 *     reference the same document — the Evidence list often cites the same
 *     doc 3-5 times across different exhibits.
 *   - ``staleTime`` is long: filenames rarely change.
 *   - ``retry: false`` so a 404 (doc not visible to the user, deleted, etc.)
 *     doesn't trigger 3× backoff retries — callers fall back to the hash
 *     tail in that case.
 */
import { useQuery } from '@tanstack/react-query'
import { lakeApi } from '@/services/api/lake'

export function useDocumentName(docId: string | null | undefined) {
  return useQuery({
    queryKey: ['document-name', docId ?? null] as const,
    queryFn: async () => {
      const doc = await lakeApi.getDocument(docId as string)
      return doc.filename ?? null
    },
    enabled: Boolean(docId),
    staleTime: 5 * 60_000,
    retry: false,
  })
}
